import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { AttachmentsPanel } from '../../components/data/AttachmentsPanel.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { EmptyState } from '../../components/ui/EmptyState.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { showToast } from '../../components/ui/Toaster.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { projectsApi } from '../../services/projectsApi.js';
import { formatCurrency, formatDate, formatNumber, todayIso, addDaysIso } from '../../utils/formatters.js';
import { KanbanBoard } from './KanbanBoard.jsx';
import { MilestoneTimeline } from './MilestoneTimeline.jsx';

function budgetPct(project) {
  const budget = Number(project?.budgetAmount) || 0;
  const actual = Number(project?.actualAmount) || 0;
  if (budget <= 0) {
    return 0;
  }
  return Math.round((actual / budget) * 100);
}

export function ProjectDetailPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const project = useQuery({ queryKey: ['projects', 'detail', id], queryFn: () => projectsApi.getProject(id).then((r) => r.items) });
  const tasks = useQuery({ queryKey: ['projects', 'tasks', id], queryFn: () => projectsApi.listTasks(id).then((r) => r.items) });
  const milestones = useQuery({ queryKey: ['projects', 'milestones', id], queryFn: () => projectsApi.listMilestones(id).then((r) => r.items) });

  const moveMutation = useMutation({
    mutationFn: ({ taskId, body }) => projectsApi.moveTask(taskId, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projects', 'tasks', id] }),
  });

  const billing = useQuery({
    queryKey: ['projects', 'billing', id],
    queryFn: () => projectsApi.billingPreview(id).then((r) => r.items),
  });

  // Resource-related billing: create the SALES invoice in finance, then stamp
  // the billed items so the preview empties and nothing is billed twice.
  const createInvoice = useMutation({
    mutationFn: async () => {
      const b = billing.data;
      /* Das Ertragskonto kommt aus den Systemkonten. Vorher stand hier '4100',
         dann '3400' als Ausweichnummer - beides Nummern, die je nach
         Kontenrahmen etwas voellig anderes bedeuten. */
      const [accounts, zwecke] = await Promise.all([
        financeApi.listAccounts().then((r) => r.items || []),
        financeApi.systemAccounts().then((r) => r.items || []).catch(() => []),
      ]);
      const zweckKonto = zwecke.find((z) => z.purpose === 'REVENUE')?.accountId;
      const revenue =
        accounts.find((a) => a.id === zweckKonto) ||
        accounts.find((a) => a.type === 'REVENUE');
      if (!revenue) throw new Error(t('projects.billing.noRevenueAccount'));
      const lines = [];
      if (Number(b.totals.timeAmount) > 0) {
        // One aggregate line per kind - quantity 1 keeps mixed hourly rates
        // cent-exact (hours × one rate would drift on blended rates).
        lines.push({
          accountId: revenue.id,
          description: t('projects.billing.lineTime', { code: b.project.code, hours: formatNumber(b.totals.hours) }),
          quantity: 1,
          unitPrice: Number(b.totals.timeAmount),
          taxRate: 8.1,
        });
      }
      if (Number(b.totals.expenseAmount) > 0) {
        lines.push({
          accountId: revenue.id,
          description: t('projects.billing.lineExpenses', { code: b.project.code }),
          quantity: 1,
          unitPrice: Number(b.totals.expenseAmount),
          taxRate: 8.1,
        });
      }
      const invoice = await financeApi.createInvoice({
        type: 'SALES',
        customerId: b.project.customerId,
        invoiceDate: todayIso(),
        dueDate: addDaysIso(30),
        notes: `${t('projects.billing.title')} ${b.project.code}`,
        lines,
      });
      try {
        await projectsApi.markProjectBilled(id, {
          invoiceId: invoice.id,
          timeEntryIds: b.timeEntries.map((e) => e.id),
          expenseIds: b.expenses.map((e) => e.id),
        });
      } catch {
        throw new Error(t('projects.billing.markFailed', { number: invoice.invoiceNumber }));
      }
      return invoice;
    },
    onSuccess: (invoice) => {
      queryClient.invalidateQueries({ queryKey: ['projects', 'billing', id] });
      showToast(t('projects.billing.done', { number: invoice.invoiceNumber }), 'success');
    },
    onError: (err) => showToast(err?.message || t('common.loadError')),
  });

  if (project.isLoading) {
    return (<section className="page"><Spinner /></section>);
  }
  if (!project.data) {
    return (<section className="page"><EmptyState message={t('projects.detail.notFound')} /></section>);
  }

  const p = project.data;
  const taskList = tasks.data || [];
  const milestoneList = milestones.data || [];
  const doneTasks = taskList.filter((task) => task.status === 'DONE').length;
  const nextMilestone = milestoneList.find((m) => !m.isDone);
  const pct = budgetPct(p);

  return (
    <section className="page">
      <div className="project-detail-head">
        <div>
          <div className="project-detail-eyebrow">{p.code}{p.customerName ? ` · ${p.customerName}` : ''}</div>
          <h1 className="project-detail-title">{p.name}</h1>
          <div className="project-detail-meta">
            {p.ownerName ? `${t('projects.detail.owner')}: ${p.ownerName} · ` : ''}
            {p.startDate ? formatDate(p.startDate) : '-'} - {p.endDate ? formatDate(p.endDate) : '-'}
          </div>
        </div>
        <span className={`status-pill status-${p.status.toLowerCase()}`}>{t(`projects.status.${p.status}`)}</span>
      </div>

      <div className="card-grid">
        <MetricCard label={t('projects.metric.budget')} value={formatCurrency(p.budgetAmount)} />
        <MetricCard
          label={t('projects.detail.spent')}
          value={formatCurrency(p.actualAmount)}
          trendVariant={pct > 100 ? 'danger' : 'success'}
        />
        <MetricCard label={t('projects.detail.tasksDone')} value={`${doneTasks} / ${taskList.length}`} />
        <MetricCard
          label={t('projects.detail.nextMilestone')}
          value={nextMilestone ? nextMilestone.title : t('common.none')}
        />
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">{t('projects.billing.title')}</div>
          {p.hourlyRate && Number(p.hourlyRate) > 0 && (
            <span className="muted text-sm">{t('projects.billing.rate')}: {formatCurrency(p.hourlyRate)}/h</span>
          )}
        </div>
        {billing.isLoading ? <Spinner /> : billing.data && (
          Number(billing.data.totals.total) > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }} className="muted">
                <span>{t('projects.billing.unbilledTime')} ({formatNumber(billing.data.totals.hours)} h)</span>
                <span className="mono">{formatCurrency(billing.data.totals.timeAmount)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }} className="muted">
                <span>{t('projects.billing.expenses')} ({billing.data.expenses.length})</span>
                <span className="mono">{formatCurrency(billing.data.totals.expenseAmount)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--color-hairline)', paddingTop: 'var(--space-2)' }}>
                <strong>{t('projects.billing.total')}</strong>
                <strong className="mono">{formatCurrency(billing.data.totals.total)}</strong>
              </div>
              {p.customerId ? (
                <div>
                  <Button variant="primary" onClick={() => createInvoice.mutate()} disabled={createInvoice.isPending}>
                    {createInvoice.isPending ? `${t('projects.billing.action')}…` : t('projects.billing.action')}
                  </Button>
                </div>
              ) : (
                <p className="muted text-sm" style={{ margin: 0 }}>{t('projects.billing.noCustomer')}</p>
              )}
            </div>
          ) : (
            <p className="muted" style={{ margin: 0 }}>{t('projects.billing.empty')}</p>
          )
        )}
      </div>

      <div className="card">
        <div className="card-header"><div className="card-title">{t('projects.detail.board')}</div></div>
        {tasks.isLoading ? <Spinner /> : taskList.length === 0 ? (
          <EmptyState message={t('projects.detail.noTasks')} />
        ) : (
          <KanbanBoard tasks={taskList} onMove={(taskId, body) => moveMutation.mutate({ taskId, body })} />
        )}
      </div>

      <div className="card">
        <div className="card-header"><div className="card-title">{t('projects.detail.milestones')}</div></div>
        {milestones.isLoading ? <Spinner /> : milestoneList.length === 0 ? (
          <EmptyState message={t('projects.detail.noMilestones')} />
        ) : (
          <MilestoneTimeline milestones={milestoneList} />
        )}
      </div>

      <AttachmentsPanel
        queryKey={['projects', 'attachments', id]}
        api={{
          list: () => projectsApi.listProjectAttachments(id).then((r) => r.items.attachments),
          upload: (body) => projectsApi.uploadProjectAttachment(id, body),
          remove: (attId) => projectsApi.deleteAttachment(attId),
          download: (attId, name) => projectsApi.downloadAttachment(attId, name),
        }}
      />
    </section>
  );
}
